import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Image,
  Button,
  useDisclosure,
  IconProps,
  Icon,
  useToast,
  Grid,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {useRef} from 'react';

import {HeaderLink, PlaygroundBackground} from '@eksab/components';
import {useCompetitionQuery} from '@eksab/features/competition/hooks';
import {RocketIcon} from '@eksab/assets/icons';
import {useMyPowersQuery} from '@eksab/features/profile/hooks';
import {BoostPrizeModal} from './modals/BoostPrizeModal';
import {BuyPowerModal} from './modals/BuyPowerModal';
import {RevertBoostModal} from './modals/RevertBoostModal';
import {CompetitionTier} from './competition-card/CompetitionTier';

type CompetitionRoute = {
  name: string;
  path: string;
  title:
    | 'competition.overview'
    | 'fantasy.lineup.create-lineup'
    | 'competition.matches'
    | 'competition.ranking'
    | 'competition.rules';
};

// TODO: Replace with a prop `routes`, passed directly to the component.
const fantasyRoutes: CompetitionRoute[] = [
  {name: 'overview', path: '', title: 'competition.overview'},
  {name: 'lineup', path: '/lineup', title: 'fantasy.lineup.create-lineup'},
  {name: 'matches', path: '/matches', title: 'competition.matches'},
  {name: 'ranking', path: '/ranking', title: 'competition.ranking'},
  {name: 'rules', path: '/rules', title: 'competition.rules'},
];

const predictionsRoutes: CompetitionRoute[] = [
  {name: 'overview', path: '', title: 'competition.overview'},
  {name: 'matches', path: '/matches', title: 'competition.matches'},
  {name: 'ranking', path: '/ranking', title: 'competition.ranking'},
  {name: 'rules', path: '/rules', title: 'competition.rules'},
];

const competitionRoutes = {
  fantasy: fantasyRoutes,
  predictions: predictionsRoutes,
};

type BoostState = Record<
  'boostable' | 'boosted' | 'disabled',
  {
    bg: string;
    iconBg: string;
    title: 'prize.booster.boosted' | 'prize.booster.boost';
    textColor: string;
  }
>;

const boostStates: BoostState = {
  boostable: {
    bg: 'linear-gradient(92.98deg, #F3189B 0%, #FB6B02 88.09%);',
    iconBg: 'black',
    title: 'prize.booster.boost',
    textColor: 'white',
  },
  boosted: {
    bg: '#A6FFC1',
    iconBg: '#5AC57B',
    title: 'prize.booster.boosted',
    textColor: '#236838',
  },
  disabled: {
    bg: '#ABABAB',
    iconBg: '#9A9A9A',
    title: 'prize.booster.boost',
    textColor: 'white',
  },
};

export function Header({competitionType}: {competitionType: Competition}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {route, query} = useRouter();
  const format = useIntl();
  const toast = useToast();

  const t = useTranslate();
  const {data: competition} = useCompetitionQuery(useRouter().query.slug as string | undefined, {
    staleTime: 5 * 60 * 1000,
  });

  const {data: boostPowers} = useMyPowersQuery({key: 'prize_booster', 'is-expired': false});

  const boostButtonState = !!competition?.entry?.booster?.user_power_id
    ? boostStates['boosted']
    : competition?.joined
      ? boostStates['boostable']
      : boostStates['disabled'];

  const {isOpen: isBoostPrizeOpen, onOpen: onBoostPrizeOpen, onClose: onBoostPrizeClose} = useDisclosure();
  const {isOpen: isBuyPowerOpen, onOpen: onBuyPowerOpen, onClose: onBuyPowerClose} = useDisclosure();
  const {isOpen: isRevertBoostOpen, onOpen: onRevertBoostOpen, onClose: onRevertBoostClose} = useDisclosure();

  const onBoostClick = () => {
    mixpanel?.track('Button Clicked', {
      button: competition?.entry?.booster?.user_power_id ? 'prize_booster.boost' : 'prize_booster.unboost',
    });
    const onClick = competition?.entry?.booster?.user_power_id
      ? onRevertBoostOpen
      : !!boostPowers?.length
        ? onBoostPrizeOpen
        : onBuyPowerOpen;
    onClick();
  };

  const onCopyCompetitionID: React.MouseEventHandler<HTMLDivElement> = (e) => {
    mixpanel?.track('Button Clicked', {button: 'competition.copy-id'});

    navigator.clipboard
      .writeText(`${competition?.id}`)
      .then(() => toast({title: t('app.success.copied'), status: 'info'}));
  };

  const formatDate = (date: string) =>
    format.formatDate(date, {
      day: 'numeric',
      month: 'short',
      year: undefined,
    });

  const routes = competitionRoutes[competitionType];

  const activeRouteIndex = ((route: string) =>
    routes.findIndex(({path}) => route.endsWith(`/${competitionType}/[slug]${path}`)))(route);

  const {width = 0, start = 0} = HeaderLink.useIndicatorProps(containerRef, activeRouteIndex) ?? {};
  const hasSponsor = competition?.sponsors[0] && competition?.sponsors[0].logo;

  return (
    <>
      <Box pos="relative">
        <PlaygroundBackground />

        <Container pt="10" flexDir="column">
          <Flex alignItems="baseline" mb="1">
            <Text
              color="green.400"
              lineHeight="5"
              fontSize={['xs', null, 'sm']}
              fontWeight={[700, null, 900]}
              textTransform="capitalize"
            >
              {competition
                ? t(`competition.header.state.${competition.featured ? 'featured' : competition.state}`)
                : t('app.competition')}
            </Text>
          </Flex>

          <Grid
            templateColumns={['1fr auto', '1fr auto auto']}
            templateAreas={[
              `
                "info league"
                "info ${hasSponsor ? 'sponsor' : 'league'}"
              `,
              `
                "info sponsor league"
              `,
            ]}
            alignItems="center"
            gap="4"
            mb="6"
          >
            <Box gridArea="info" me="auto">
              <Heading
                color="white"
                mb="1.5"
                width="fit-content"
                fontSize="clamp(2rem, 3.7vw, 3.25rem)"
                lineHeight="1.46"
              >
                {competition?.name}
              </Heading>

              <Text
                display="flex"
                flexDir={['column', null, 'row']}
                alignItems={['flex-start', null, 'center']}
                gap="3"
                color="white"
                fontWeight="500"
                fontSize={['md', null, 'xl']}
                mb={[1, 2, 2.5]}
              >
                {competition &&
                  t('competition.card.live-from-to', {
                    from: formatDate(competition.season.start ?? ''),
                    to: formatDate(competition.season.end ?? ''),
                  })}

                {competition?.tier === 'flex' && <CompetitionTier tier={competition.tier} />}
              </Text>

              <Flex
                w="fit-content"
                align="center"
                gap="2"
                mb="4"
                userSelect="text"
                _selection={{bg: 'red'}}
                cursor="pointer"
                onClick={onCopyCompetitionID}
              >
                <Flex align="center" gap="1">
                  <TagIcon />

                  <Text fontSize="sm" fontWeight="500" color="white">
                    {t('app.id')}#
                  </Text>
                </Flex>

                <Divider />

                <Text fontSize="sm" fontWeight="700" color="gray.400">
                  {competition?.id}
                </Text>
              </Flex>

              {competition?.boostable && (
                <Button
                  ms="auto"
                  fontSize="md"
                  fontWeight="700"
                  textTransform="initial"
                  cursor="pointer"
                  paddingInlineEnd="4"
                  paddingInlineStart="2"
                  pt="2"
                  pb="2"
                  bg={boostButtonState?.bg}
                  rounded="34px"
                  gap="2"
                  textColor={boostButtonState?.textColor}
                  _hover={{}}
                  _active={{}}
                  disabled={
                    !competition?.joined ||
                    (!!competition?.entry?.booster?.user_power_id && !competition?.is_withdrawable) ||
                    competition?.state !== 'upcoming'
                  }
                  _disabled={{
                    cursor: 'not-allowed',
                    bg: boostButtonState?.bg,
                  }}
                  onClick={onBoostClick}
                >
                  <Box rounded="full" p="2" bg={boostButtonState?.iconBg}>
                    <RocketIcon />
                  </Box>
                  {t(boostButtonState?.title)}
                </Button>
              )}
            </Box>

            {hasSponsor && (
              <Flex
                gridArea="sponsor"
                as="a"
                flexDir="column"
                gap="3"
                align="center"
                href={competition.sponsors[0]?.logo_link ?? undefined}
                target="_blank"
                cursor={competition.sponsors[0]?.logo_link ? 'pointer' : 'initial'}
              >
                <Image
                  w="auto"
                  maxH={[16, 20, 28, 32]}
                  objectFit="contain"
                  src={competition.sponsors[0]?.logo?.original}
                  alt={competition.sponsors[0]?.name}
                />

                <Text align="center" color="gray.300" fontSize="x-small" fontWeight="700" flexShrink={0}>
                  {t('competition.sponsored_by')}
                </Text>
              </Flex>
            )}

            {competition?.leagues[0]?.logo?.original && (
              <Image
                gridArea="league"
                alt="league"
                objectFit="contain"
                w="clamp(60px, 18vw, 150px)"
                h="clamp(60px, 18vw, 150px)"
                src={competition.leagues[0].logo.original}
              />
            )}
          </Grid>

          <Container ref={containerRef} variant="scroll" pb="2" gap="9" pos="relative">
            {routes.map(({name, path, title}, index) => (
              <HeaderLink
                key={name}
                title={t(title)}
                href={`${route.startsWith('/groups') ? `/groups/${query.groupId}` : ''}/${competitionType}/${
                  query.slug
                }${path}`}
                isActive={index === activeRouteIndex}
              />
            ))}

            <HeaderLink.Indicator width={width} start={start} />
          </Container>
        </Container>
      </Box>

      <BoostPrizeModal
        isOpen={isBoostPrizeOpen}
        onClose={onBoostPrizeClose}
        powers={boostPowers}
        competitionEntryId={competition?.entry?.id as number}
        competitionId={competition?.id as number}
      />
      <BuyPowerModal isOpen={isBuyPowerOpen} onClose={onBuyPowerClose} />
      <RevertBoostModal
        isOpen={isRevertBoostOpen}
        onClose={onRevertBoostClose}
        competitionEntryId={competition?.entry?.id as number}
        boostId={competition?.entry?.booster?.user_power_id as number}
        competitionId={competition?.id as number}
      />
    </>
  );
}

const TagIcon = (props: IconProps) => (
  <Icon width="14px" height="15px" viewBox="0 0 14 15" fill="none" color="white" {...props}>
    <path
      d="M10.1314 10.595C7.54028 13.1749 7.11847 12.7549 2.41834 8.01514C1.15292 6.75526 1.99653 3.33541 2.41834 2.91544C2.84014 2.49546 6.27486 1.65551 7.54028 2.91544C12.2404 7.5952 12.6622 8.01514 10.1314 10.595Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.37565 5.16536C4.53673 5.16536 4.66732 5.03478 4.66732 4.8737C4.66732 4.71262 4.53673 4.58203 4.37565 4.58203C4.21457 4.58203 4.08398 4.71262 4.08398 4.8737C4.08398 5.03478 4.21457 5.16536 4.37565 5.16536Z"
      fill="currentColor"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
);

const Divider = (props: IconProps) => (
  <Icon width="1px" height="11px" viewBox="0 0 1 11" fill="none" {...props}>
    <line x1="0.25" y1="0.5" x2="0.25" y2="10.5" stroke="#DAE2DA" strokeWidth="0.5" />
  </Icon>
);
